import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Spinner,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { formatInLanguage } from '@/utils/dateLocale';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ProductsStepHeaderProps = {
    fileName: string;
    /** The file's data rows. */
    rowCount: number;
    /** When the preview the table shows was answered, ISO date-time; `null` before. */
    lastCheckedAt: string | null;
    /** A preview is running: Re-check shows it and waits. */
    isChecking: boolean;
    /** The import is running: Re-check waits for it. */
    isDisabled?: boolean;
    onRecheck: () => void;
};

// The title is the step's only page-level heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

/** The last check's time of day, in the display zone. */
const CHECK_TIME_FORMAT = 'HH:mm';

/**
 * The Products step's header: the title and what the statuses mean, the
 * file line ("catalog.xlsx · 1,411 rows · Last check 13:49") and Re-check,
 * which shows a spinner and "Checking rows…" while any preview runs and is
 * disabled while the import runs. On a phone the button takes the full
 * width under the text.
 */
export const ProductsStepHeader: React.FC<ProductsStepHeaderProps> = ({
    fileName,
    rowCount,
    lastCheckedAt,
    isChecking,
    isDisabled = false,
    onRecheck,
}) => {
    const { t, i18n } = useTranslation('pages');

    return (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Typography
                    render={asH1}
                    type="h2"
                    {...getNodeTestIdProp('catalog-import-products-title')}
                >
                    {t('catalogImport.products.title')}
                </Typography>
                <Typography color="muted" type="body-sm">
                    {t('catalogImport.products.intro')}
                </Typography>
                <Typography
                    type="body-sm"
                    {...getNodeTestIdProp('catalog-import-products-file')}
                >
                    {[
                        fileName,
                        t('catalogImport.products.rows', {
                            count: rowCount,
                            rows: countParam(rowCount),
                        }),
                        lastCheckedAt === null
                            ? null
                            : t('catalogImport.products.lastCheck', {
                                  time: formatInLanguage(
                                      lastCheckedAt,
                                      CHECK_TIME_FORMAT,
                                      i18n.language,
                                  ),
                              }),
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </Typography>
            </div>
            <Button
                className="w-full sm:w-auto"
                isDisabled={isDisabled}
                isPending={isChecking}
                onPress={onRecheck}
                variant="secondary"
                {...getNodeTestIdProp('catalog-import-products-recheck')}
            >
                {({ isPending }) => (
                    <>
                        {isPending ? (
                            <Spinner color="current" size="sm" />
                        ) : (
                            <Icon
                                aria-hidden
                                icon="gravity-ui:arrows-rotate-right"
                            />
                        )}
                        {t(
                            isPending
                                ? 'catalogImport.products.checking'
                                : 'catalogImport.products.recheck',
                        )}
                    </>
                )}
            </Button>
        </div>
    );
};
